/**
 * Shared types for the package and subscription components. CLIENT-AGNOSTIC: a
 * component takes the `api` client as a prop (both apps, web_admin and web_crm,
 * have exactly this shape in `lib/api`), so the same component renders in both.
 */
export interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  del: <T>(path: string) => Promise<T>;
}

export type Locale = "et" | "en";
export type LocaleMap = Record<string, string>;

export type EntitlementValue = boolean | number;
export type EntitlementMap = Record<string, EntitlementValue>;

/** An unlimited limit/quota (∞). Matches the backend (lib/entitlements). */
export const UNLIMITED = -1;

export interface EntitlementCatalogEntry {
  key: string;
  type: "boolean" | "limit" | "quota";
  category: string;
  /** Exclusive-widget access tier (1 Effect / 2 Signature / 3 Flagship), served by the API
   *  on `widget.*` keys only. The plan cards roll every widget row of one tier into a
   *  single line, so the ladder lives in the API catalog and not in a UI mirror. */
  tier?: 1 | 2 | 3;
  /** What a new package starts with (the monitoring catalog; the platform one has none). */
  default?: EntitlementValue;
  /** Bounds of a number. A bounded number has no "unlimited" (the monitoring interval). */
  min?: number;
  max?: number;
  /** Promised by a package, delivered by nothing yet: the editor marks the row. */
  planned?: boolean;
}

/** The display bullet list per locale: { et: string[], en: string[] }. */
export type HighlightsMap = Record<string, string[]>;

export interface PlanDTO {
  code: string;
  productCode: string;
  name: LocaleMap;
  description: LocaleMap | null;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  currency: string;
  features: EntitlementMap;
  /** The "what the package includes" bullet list (packages outside the platform). */
  highlights: HighlightsMap | null;
  /** The highlighted card in the public pricing widget. */
  featured: boolean;
  /** A short label (e.g. "Most popular"). */
  badge: LocaleMap | null;
  isPublic: boolean;
  sortOrder: number;
  providerPriceIds: unknown;
  /** Free trial (days); 0 = off. Applies at a NEW subscription's checkout. */
  trialDays: number;
  subscriberCount: number;
  /**
   * Watched sites on the package, for a package of the monitoring service
   * (Veebihaldus) only. Null when the CRM database did not answer.
   */
  siteCount?: number | null;
}

/** A product/service the packages group under. The `isPlatform` product drives the
 *  entitlements and subscriptions, the `monitoring` one (Veebihaldus) drives site
 *  monitoring, and the rest are display-only price lists. */
export interface ProductDTO {
  code: string;
  name: LocaleMap;
  description: LocaleMap | null;
  /** A lucide icon name (kebab), e.g. "server"; empty -> the default icon. */
  icon: string | null;
  isPlatform: boolean;
  /** Its packages drive site monitoring (docs/saidiseire-paketid.md). */
  monitoring?: boolean;
  sortOrder: number;
  /** How many packages the product has (only from the `/admin/products` list). */
  planCount?: number;
}

export interface PlansAllResponse {
  plans: PlanDTO[];
  /** The platform catalog. */
  catalog: EntitlementCatalogEntry[];
  /** The monitoring catalog, for the packages of the `monitoring` service. */
  monitorCatalog?: EntitlementCatalogEntry[];
  products: ProductDTO[];
}

export interface AddOnRef {
  addOnCode: string;
  quantity: number;
}

export interface SubscriptionRow {
  siteId: string;
  /** The account (the billing unit) - a manual refund targets the account. Host/no account -> null. */
  accountId: string | null;
  siteName: string;
  siteSlug: string;
  siteCreatedAt: string;
  hasSubscription: boolean;
  planCode: string;
  planName: LocaleMap | null;
  status: string;
  billingCycle: string;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** Payment gateway (F2): "stripe" | "montonio" | null (manual / no subscription). */
  provider: string | null;
  providerCustomerId: string | null;
  providerSubscriptionId: string | null;
  addOns: AddOnRef[];
  owner: { email: string; name: string; roleKey: string } | null;
  memberCount: number;
}

/** The Stripe gateway's configuration status (`GET /api/admin/billing/status`). */
export interface BillingProviderStatus {
  configured: boolean;
  payablePlans: number;
  plansSynced: number;
}

/** The summary of `POST /api/admin/billing/sync-plans`. */
export interface BillingSyncSummary {
  configured: boolean;
  synced: number;
  skipped: number;
  plans: { code: string; productId: string; monthly: string | null; yearly: string | null }[];
}

export interface SubscriptionsListResponse {
  rows: SubscriptionRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SubscriptionStats {
  /** Client sites (host excluded) - one per row of the table below. */
  total: number;
  /** Catalogue list price, summed over subscriptions (not over sites). */
  mrrCents: number;
  /** Stripe-actual monthly revenue (after coupons); falls back to the catalogue. */
  realMrrCents: number;
  /** EVERY platform package, including ones nobody has subscribed to yet. */
  byPlan: { planCode: string; planName: LocaleMap | null; count: number }[];
  byStatus: { status: string; count: number }[];
}

export interface SubscriptionDetail {
  siteId: string;
  siteName: string;
  siteSlug: string;
  isHost: boolean;
  subscription: {
    planCode: string;
    status: string;
    billingCycle: string;
    currentPeriodEnd: string | null;
    trialEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    addOns: AddOnRef[];
  } | null;
  entitlements: { planCode: string; status: string; unlimited: boolean; features: EntitlementMap };
  members: { email: string; name: string; roleKey: string; roleName: LocaleMap }[];
}

export const SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "canceled", "paused"] as const;
export const BILLING_CYCLES = ["monthly", "yearly"] as const;

/** One Stripe-computed invoice line in a plan-change preview (description + amount). */
export interface PlanChangePreviewLine {
  /** Stripe's own description (in English) - the fallback when `kind` is missing. */
  description: string;
  amountCents: number;
  /** A proration line (part-period credit/debit) as opposed to a regular one. */
  proration: boolean;
  /** The proration line's kind, for a localized label (old credit vs the new part). */
  kind?: "unused" | "remaining" | null;
  /** The package name (i18n) the line refers to - for building the label. */
  planName?: LocaleMap | null;
  /** The proration date (ISO) - "... after <date>". */
  atDate?: string | null;
}

/** ABSOLUTE target quantity for an add-on (not a delta) - `POST /api/billing/preview|checkout`. */
export interface AddOnTargetInput {
  code: string;
  quantity: number;
}

/** One account site in the quota picker ("which sites do I keep?"). */
export interface QuotaOptionSite {
  id: string;
  name: string;
  host: string | null;
  status: string;
  createdAt: string;
}

/**
 * Quota block for the plan-change dialog: how many active sites the account has, how many
 * the target plan (plus the add-ons that survive) allows and - when short - what
 * `extra_site` would cost plus the sites to choose from. `null` = the change fits (the
 * dialog stays quiet on an upgrade).
 *
 * NOTE: `allowed` and `activeCount` are ADD-ON AWARE and count only ACTIVE sites - the old
 * `quotaWarning` read the raw plan field and every site row, including closed ones.
 */
export interface QuotaOptions {
  activeCount: number;
  /** -1 = ∞. */
  allowed: number;
  /** How many active sites would be left uncovered. */
  deficit: number;
  extraSite: {
    code: string;
    name: LocaleMap | null;
    currentQuantity: number;
    quantityNeeded: number;
    /** Absolute quantity to send back in `addOns`. */
    targetQuantity: number;
    unitPriceCents: number;
    currency: string;
  } | null;
  sites: QuotaOptionSite[];
}

/** One limit that would end up below current usage. */
export interface EntitlementImpact {
  key: string;
  used: number;
  /** -1 = ∞. */
  newLimit: number;
  over: boolean;
  /** Worst-affected site for per-site metrics (storage, AI). */
  siteId?: string;
  siteName?: string;
}

/** "What do I lose?" - advisory, never blocking (except the site quota, enforced server-side). */
export interface ChangeImpacts {
  entitlements: EntitlementImpact[];
  /** Boolean features that would switch off (e.g. `sections.accountShare`). */
  lostFeatures: string[];
}

/**
 * A PREVIEW of a plan or cycle change (`POST /api/billing/preview`) - the result of
 * Stripe's `invoices.createPreview`, WITHOUT billing. The confirmation dialog shows it
 * before the real change. `change`:
 *  - `new`      -> no live subscription, the change would go to Stripe Checkout (no dialog).
 *  - `none`     -> already on exactly this price, nothing changes.
 *  - `upgrade`  -> `amountCents` is paid NOW (proration).
 *  - `downgrade`-> the unused credit applies to the next invoice (`amountCents` often 0).
 */
export interface PlanChangePreview {
  change: "new" | "none" | "upgrade" | "downgrade";
  currency: string;
  /** The amount of the invoice this change creates (upgrade -> payable now). */
  amountCents: number;
  /** The regular price from now on in the chosen cycle (from the catalog). */
  recurringCents: number;
  billingCycle: "monthly" | "yearly";
  /** The date of the next regular invoice (ISO), or null when unknown. */
  nextRenewalAt?: string | null;
  /** Quota warning (soft v1): the target plan's `sites.max` < the account's site count. */
  quotaWarning?: { current: number; allowed: number } | null;
  /** Quota resolution (add-on OR site picker) - the richer successor to `quotaWarning`. */
  quotaOptions?: QuotaOptions | null;
  /** What the change would take away from the account (limits + features). */
  impacts?: ChangeImpacts | null;
  lines: PlanChangePreviewLine[];
}
